import {handleError, toastError} from "helpers"
import {useModel} from "hooks"
import {get, groupBy, head, isEmpty, isString, keyBy, map, orderBy} from "lodash"
import moment from "moment"
import {taskService, userService} from "services"
import {DigitalDocumentFieldSetUp} from "types/tasks"
import {uploadUsingPreSignedUrl} from "helpers/chat"
import {useConditionalField} from "./useConditionalField"

export const useSubmitTask = () => {
    const model = useModel()
    const dateFormat = model.getUserDateFormat()
    const {checkFieldCanShow} = useConditionalField()

    const validateFieldBeforeSubmit = ({fields, fieldRefs, setErrorFieldIds}) => {
        const fieldsGroupByParentId = groupBy(fields, "parentId")

        const errorFields = fields.filter((field) => {
            const canShow = checkFieldCanShow({field, fields})
            if (!canShow) {
                return false
            }
            if (
                (field.type === DigitalDocumentFieldSetUp.CHECKBOX ||
                    field.type === DigitalDocumentFieldSetUp.INITIAL) &&
                field.isYourField &&
                field.required
            ) {
                const hasSigned = (fieldsGroupByParentId[field.parentId] || []).some(
                    (fieldItem) => fieldItem.isSigned && fieldItem.value
                )
                return !hasSigned
            }

            return field.isYourField && field.required && !field.value
        })

        const errorFieldIds: string[] = orderBy(errorFields, ["placeInformation.top", "placeInformation.left"]).map(
            (field) => field.id
        )
        setErrorFieldIds(errorFieldIds)
        if (errorFieldIds.length) {
            const firstErrorField = fieldRefs.current[head(errorFieldIds)]
            firstErrorField?.scrollIntoView({behavior: "smooth", block: "center", inline: "center"})
            toastError("There are required fields need to be filled")
            return true
        }

        return false
    }

    const getAnswer = async ({fields, currentSignature, currentInitials}) => {
        let answers: any = {}
        let hasSignature = false
        let hasInitials = false

        const yourFields = fields.filter((field) => field.isYourField && checkFieldCanShow({field, fields}))
        for (const field of yourFields) {
            switch (field.type) {
                case DigitalDocumentFieldSetUp.SIGNATURE:
                    if (!hasSignature && field.value) {
                        hasSignature = true
                    }
                    answers[field.id] = field.value ? "signed" : "not-signed"
                    break
                case DigitalDocumentFieldSetUp.INITIAL:
                    if (field.parentId) {
                        answers[field.parentId] = answers[field.parentId] || ""
                        if (!answers[field.parentId]) {
                            if (!hasInitials && field.value) {
                                hasInitials = true
                            }
                            answers[field.parentId] += field.value ? "signed" : "not-signed"
                        } else {
                            if (!hasInitials && field.value) {
                                hasInitials = true
                            }
                            answers[field.parentId] += field.value ? ",signed" : ",not-signed"
                        }
                    }
                    break
                case DigitalDocumentFieldSetUp.CHECKBOX:
                    if (field.parentId) {
                        answers[field.parentId] = answers[field.parentId] || ""
                        if (!answers[field.parentId]) {
                            answers[field.parentId] = field.value ? "Yes" : "No"
                        } else {
                            answers[field.parentId] += field.value ? ",Yes" : ",No"
                        }
                    }
                    break
                case DigitalDocumentFieldSetUp.SELECT:
                    answers[field.id] = field.value?.option || ""
                    break
                case DigitalDocumentFieldSetUp.TEXT:
                    answers[field.id] = field.value || ""
                    if (field.dataType === "date" && field.value) {
                        answers[field.id] = moment(field.value).format(dateFormat)
                    }
                    break
                case DigitalDocumentFieldSetUp.GRADE:
                    answers[field.id] = +field.value || null
                    break
                case DigitalDocumentFieldSetUp.FILE: {
                    const filesExtensions = map(field.value, ({extension, id}) => ({fileExtension: extension, id}))
                    if (!isEmpty(filesExtensions) && !isString(head(field.value))) {
                        const uploadAwsFilePayloads: Promise<any>[] = []
                        const uploadedFiles = await taskService.createUploadLink({files: filesExtensions})
                        const filesById = keyBy(field.value, "id")
                        for (const item of uploadedFiles) {
                            const file = get(filesById[item.id], "file")
                            uploadAwsFilePayloads.push(uploadUsingPreSignedUrl(item.urlForUploading, file))
                        }
                        await Promise.all(uploadAwsFilePayloads)
                        const fileUrls = uploadedFiles.map((item) => item.url)
                        answers[field.id] = fileUrls
                    } else {
                        answers[field.id] = field.value || null
                    }
                    break
                }
                default:
                    break
            }
        }

        if (hasSignature) {
            try {
                const images = await userService.getBase64Img([currentSignature.imageUrl])
                answers.signature = get(images, "[0].base64", "")
            } catch (error) {
                handleError(error)
            }
        }
        if (hasInitials) {
            try {
                const images = await userService.getBase64Img([currentInitials.imageUrl])
                answers.initials = get(images, "[0].base64", "")
            } catch (error) {
                handleError(error)
            }
        }

        return {answers, hasSignature, hasInitials}
    }

    return {getAnswer, validateFieldBeforeSubmit}
}
